import {WidgetType} from '@codemirror/view'
import type {Element} from 'hast'

import styles from './video-element.module.css'

type VideoElementProperties = NonNullable<Element['properties']> & {
  dataOriginalHref?: string
  dataOriginalText?: string
}

type LinkOptions = {
  href: string
  withTitle?: boolean
}

const createVideoLinkElement = ({href, withTitle = false}: LinkOptions) => {
  const link = document.createElement('a')
  link.href = href
  link.rel = 'noreferrer noopener'
  link.textContent = href
  link.className = styles.link
  link.target = '_blank'
  if (withTitle) link.title = href
  return link
}

const getOriginalHref = (value: string | undefined) => {
  if (!value) return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

export class VideoElementWidget extends WidgetType {
  #node: Element
  #className: string | undefined

  constructor(node: Element, className?: string) {
    super()
    this.#node = node
    this.#className = className
  }

  override eq(other: WidgetType) {
    return other instanceof VideoElementWidget && other.#node === this.#node && other.#className === this.#className
  }

  override toDOM() {
    const container = document.createElement('div')
    container.className = styles.container

    if (this.#className) {
      for (const className of this.#className.split(/\s+/)) {
        if (className) container.classList.add(className)
      }
    }

    const videoElement = document.createElement('video')
    videoElement.classList.add(styles.video)

    const {
      dataOriginalHref,
      dataOriginalText: _dataOriginalText,
      ...restProperties
    } = (this.#node.properties ?? {}) as VideoElementProperties

    for (const [key, value] of Object.entries(restProperties)) {
      if (value === true) {
        videoElement.setAttribute(key, '')
      } else if (value === false || value === undefined || value === null) {
        continue
      } else {
        videoElement.setAttribute(key, String(value))
      }
    }

    const originalHref = getOriginalHref(dataOriginalHref)
    if (originalHref) videoElement.setAttribute('data-original-href', originalHref)

    let fallbackShown = false
    const showFallback = () => {
      if (fallbackShown) return
      fallbackShown = true
      const fallbackMessage = document.createElement('div')
      fallbackMessage.className = styles.fallback
      fallbackMessage.textContent = 'Video preview unavailable.'
      if (originalHref) {
        const link = createVideoLinkElement({href: originalHref})
        fallbackMessage.append(' ')
        fallbackMessage.append(link)
      }
      container.insertBefore(fallbackMessage, container.lastChild)
    }

    videoElement.addEventListener('error', showFallback)
    container.append(videoElement)

    if (originalHref) {
      const link = createVideoLinkElement({href: originalHref, withTitle: true})
      container.append(link)
    }

    return container
  }

  override ignoreEvent() {
    return false
  }
}
